import { useEffect, useRef, useCallback, useState } from "react";
import type { Booking } from "@scheduler/shared";

interface WebSocketMessage {
  type: "schedule_updated" | "booking_created" | "booking_cancelled" | "pong";
  bookings?: Booking[];
  booking?: Booking;
}

export function useRoomWebSocket(
  roomId: string | undefined,
  onUpdate: (message: WebSocketMessage) => void,
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (!roomId) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/room/${roomId}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log(`WebSocket connected for room ${roomId}`);
      setConnected(true);

      // Send ping every 30 seconds to keep connection alive
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        onUpdate(message);
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    ws.onclose = () => {
      console.log(`WebSocket disconnected for room ${roomId}`);
      setConnected(false);
      clearInterval(pingIntervalRef.current);
      // Reconnect after 3 seconds
      reconnectTimerRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      ws.close();
    };
  }, [roomId, onUpdate]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { ws: wsRef, connected };
}
